import React, { useState, useEffect } from 'react';
import { 
  Box, Stack, Group, Title, Button, Card, Text, Badge, Modal, 
  TextInput, Textarea, Select, ActionIcon, Flex, ScrollArea, Grid, 
  Paper, ThemeIcon, Divider, SimpleGrid, NumberInput, Alert, SegmentedControl, TagsInput, Loader, Drawer, Timeline
} from '@mantine/core';
import { 
  IconPlus, IconTrash, IconEdit, IconRocket, IconBrandGoogle, 
  IconBrandFacebook, IconRobot, IconCheck, IconAlertCircle, IconSpeakerphone, IconTrendingUp,
  IconHistory, IconX, IconLoader2 
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

const AD_TRANSLATIONS = {
  pl: {
    title: "Kampanie Reklamowe (Google Ads & Meta)",
    desc: "Automatyczne tworzenie kampanii na podstawie Twoich usług przy użyciu asystenta AI i natychmiastowa publikacja.",
    generateAi: "Wygeneruj z AI 🤖",
    analyzing: "AI analizuje usługi...",
    newCampaign: "Nowa Kampania",
    allPlatforms: "Wszystkie platformy",
    showing: "Wyświetlano:",
    campaigns: "kampanii",
    headline: "Nagłówek reklamowy:",
    keywords: "Słowa kluczowe / Tagi:",
    dailyBudget: "Budżet dzienny:",
    perDay: "PLN / dzień",
    edit: "Edytuj",
    published: "Opublikowana",
    publish: "Publikuj 🚀",
    noCampaigns: "Brak kampanii reklamowych",
    noCampaignsDesc: "Nie masz jeszcze żadnych utworzonych kampanii dla tej platformy. Kliknij przycisk generatora AI, aby automatycznie stworzyć reklamy!",
    editModalTitle: "✏️ Edytuj Kampanię",
    newModalTitle: "🚀 Nowa Kampania Reklamowa",
    formPlatform: "Platforma reklamowa",
    formPlatformDesc: "Wybierz gdzie kampania ma zostać opublikowana",
    formName: "Nazwa kampanii w systemie",
    formNamePlaceholder: "np. Kampania Letnia - CRM AI",
    formHeadline: "Nagłówek reklamowy (Headline)",
    formHeadlineDesc: "Dla Google Ads zalecane do 30 znaków",
    formHeadlinePlaceholder: "np. Nowoczesny CRM z AI | Wypróbuj",
    formDesc: "Tekst reklamy (Description)",
    formDescDesc: "Perswazyjny opis z wezwaniem do działania (CTA)",
    formDescPlaceholder: "np. Zwiększ sprzedaż w swojej firmie i zaoszczędź czas...",
    formKeywords: "Słowa kluczowe (po przecinku)",
    formKeywordsPlaceholder: "crm, system ai, automatyzacja, leady",
    formBudget: "Budżet dzienny (w PLN)",
    formBudgetDesc: "Maksymalna kwota, jaką chcesz wydawać dziennie",
    formStatus: "Status kampanii",
    cancel: "Anuluj",
    save: "Zapisz kampanię",
    statusDraft: "📝 Szkic (DRAFT)",
    statusApproved: "✅ Zatwierdzona (APPROVED)",
    statusPublished: "🚀 Opublikowana (PUBLISHED)",
    confirmDelete: "Czy na pewno chcesz usunąć tę kampanię?",
    publishingTo: "🚀 Wysyłanie kampanii do",
    publishingSuccess: "Po połączeniu z kontem reklamowym kampania zostanie automatycznie aktywowana.",
    generateError: "Nie udało się wygenerować kampanii. Sprawdź połączenie z Agentic AI.",
    generateAiTitle: "Wygeneruj Kampanie Reklamowe z AI",
    generateAiDesc: "Nasz asystent AI (Agentic AI) przeanalizuje Twoje usługi z cennika i automatycznie przygotuje gotowe nagłówki, opisy oraz propozycje słów kluczowych dla Google Ads i Facebook Ads.",
    aiContextLabel: "💡 Dodatkowy kontekst / wytyczne dla AI (opcjonalnie):",
    aiContextDesc: "Możesz wpisać szczegółowe wskazówki, np. aktualną promocję, grupę docelową lub ton wypowiedzi.",
    aiContextPlaceholder: "np. Skup się na promocji jesiennej z rabatem 20% dla klientów B2B, stwórz pilne wezwanie do działania...",
    generateAiStart: "✨ Rozpocznij generowanie",
    aiTasks: "Zadania AI",
    aiTasksDrawerTitle: "Zadania AI w tle",
    noTasks: "Brak zleconych zadań.",
    statusLabel: "Status:",
    jobTypeGenerate: "Generowanie Kampanii (AI)",
    jobResultWaiting: "Oczekiwanie w kolejce...",
    jobResultSuccess: "Wygenerowano kampanie pomyślnie",
    jobResultNoCampaigns: "Brak wyników z AI"
  },
  en: {
    title: "Ad Campaigns (Google Ads & Meta)",
    desc: "Automatic campaign generation based on your services using AI assistant with instant publishing.",
    generateAi: "Generate with AI 🤖",
    analyzing: "AI analyzing services...",
    newCampaign: "New Campaign",
    allPlatforms: "All platforms",
    showing: "Showing:",
    campaigns: "campaigns",
    headline: "Ad headline:",
    keywords: "Keywords / Tags:",
    dailyBudget: "Daily budget:",
    perDay: "PLN / day",
    edit: "Edit",
    published: "Published",
    publish: "Publish 🚀",
    noCampaigns: "No advertising campaigns",
    noCampaignsDesc: "You don't have any campaigns created for this platform yet. Click the AI generator button to automatically create ads!",
    editModalTitle: "✏️ Edit Campaign",
    newModalTitle: "🚀 New Ad Campaign",
    formPlatform: "Ad Platform",
    formPlatformDesc: "Choose where the campaign should be published",
    formName: "Campaign Name",
    formNamePlaceholder: "e.g. Summer Campaign - CRM AI",
    formHeadline: "Ad Headline",
    formHeadlineDesc: "Recommended up to 30 characters for Google Ads",
    formHeadlinePlaceholder: "e.g. Modern CRM with AI | Try Now",
    formDesc: "Ad Description",
    formDescDesc: "Persuasive text with Call To Action (CTA)",
    formDescPlaceholder: "e.g. Boost sales in your company and save time...",
    formKeywords: "Keywords (comma separated)",
    formKeywordsPlaceholder: "crm, ai system, automation, leads",
    formBudget: "Daily budget (in PLN)",
    formBudgetDesc: "Maximum amount you want to spend per day",
    formStatus: "Campaign Status",
    cancel: "Cancel",
    save: "Save Campaign",
    statusDraft: "📝 Draft (DRAFT)",
    statusApproved: "✅ Approved (APPROVED)",
    statusPublished: "🚀 Published (PUBLISHED)",
    confirmDelete: "Are you sure you want to delete this campaign?",
    publishingTo: "🚀 Sending campaign to",
    publishingSuccess: "The campaign will be activated once connected to your ad account.",
    generateError: "Failed to generate campaigns with AI. Please check logs.",
    generateAiTitle: "Generate Ad Campaigns with AI",
    generateAiDesc: "Our AI assistant (Agentic AI) will analyze your pricing services and automatically create headlines, descriptions, and keyword suggestions for Google Ads and Facebook Ads.",
    aiContextLabel: "💡 Additional context / instructions for AI (optional):",
    aiContextDesc: "You can enter specific instructions, e.g., current promotions, target audience, or tone of voice.",
    aiContextPlaceholder: "e.g. Focus on autumn promotion with 20% B2B discount, create an urgent call to action...",
    generateAiStart: "✨ Start Generating",
    aiTasks: "AI Tasks",
    aiTasksDrawerTitle: "Background AI Tasks",
    noTasks: "No background tasks.",
    statusLabel: "Status:",
    jobTypeGenerate: "Campaign Generation (AI)",
    jobResultWaiting: "Waiting in queue...",
    jobResultSuccess: "Campaigns generated successfully",
    jobResultNoCampaigns: "No campaigns generated"
  },
  de: {
    title: "Werbekampagnen (Google Ads & Meta)",
    desc: "Automatische Kampagnenerstellung basierend auf Ihren Diensten mit dem KI-Assistenten und sofortiger Veröffentlichung.",
    generateAi: "Mit KI generieren 🤖",
    analyzing: "KI analysiert Dienste...",
    newCampaign: "Neue Kampagne",
    allPlatforms: "Alle Plattformen",
    showing: "Angezeigt:",
    campaigns: "Kampagnen",
    headline: "Werbeüberschrift:",
    keywords: "Schlüsselwörter / Tags:",
    dailyBudget: "Tagesbudget:",
    perDay: "PLN / Tag",
    edit: "Bearbeiten",
    published: "Veröffentlicht",
    publish: "Veröffentlichen 🚀",
    noCampaigns: "Keine Werbekampagnen",
    noCampaignsDesc: "Sie haben noch keine Kampagnen für diese Plattform erstellt. Klicken Sie auf die KI-Generator-Schaltfläche!",
    editModalTitle: "✏️ Kampagne bearbeiten",
    newModalTitle: "🚀 Neue Werbekampagne",
    formPlatform: "Werbeplattform",
    formPlatformDesc: "Wählen Sie, wo die Kampagne veröffentlicht werden soll",
    formName: "Kampagnenname",
    formNamePlaceholder: "z.B. Sommerkampagne - CRM KI",
    formHeadline: "Werbeüberschrift",
    formHeadlineDesc: "Empfohlen bis zu 30 Zeichen für Google Ads",
    formHeadlinePlaceholder: "z.B. Modernes CRM mit KI | Jetzt testen",
    formDesc: "Werbetext (Description)",
    formDescDesc: "Überzeugender Text mit Handlungsaufruf (CTA)",
    formDescPlaceholder: "z.B. Steigern Sie Ihren Umsatz und sparen Sie Zeit...",
    formKeywords: "Schlüsselwörter (kommagetrennt)",
    formKeywordsPlaceholder: "crm, ki system, automatisierung, leads",
    formBudget: "Tagesbudget (in PLN)",
    formBudgetDesc: "Maximaler Betrag, den Sie pro Tag ausgeben möchten",
    formStatus: "Kampagnenstatus",
    cancel: "Abbrechen",
    save: "Kampagne speichern",
    statusDraft: "📝 Entwurf (DRAFT)",
    statusApproved: "✅ Genehmigt (APPROVED)",
    statusPublished: "🚀 Veröffentlicht (PUBLISHED)",
    confirmDelete: "Möchten Sie diese Kampagne wirklich löschen?",
    publishingTo: "🚀 Sende Kampagne an",
    publishingSuccess: "Die Kampagne wird aktiviert, sobald sie mit Ihrem Werbekonto verbunden ist.",
    generateError: "Fehler beim Generieren von Kampagnen mit KI.",
    aiTasks: "Zadania AI",
    aiTasksDrawerTitle: "Zadania AI w tle",
    noTasks: "Brak zleconych zadań.",
    statusLabel: "Status:",
  },
  fr: {
    title: "Campagnes Publicitaires (Google Ads & Meta)",
    desc: "Génération automatique de campagnes basées sur vos services via l'assistant IA avec publication instantanée.",
    generateAi: "Générer avec l'IA 🤖",
    analyzing: "L'IA analyse les services...",
    newCampaign: "Nouvelle Campagne",
    allPlatforms: "Toutes les plateformes",
    showing: "Affichage :",
    campaigns: "campagnes",
    headline: "Titre publicitaire :",
    keywords: "Mots-clés / Tags :",
    dailyBudget: "Budget quotidien :",
    perDay: "PLN / jour",
    edit: "Modifier",
    published: "Publiée",
    publish: "Publier 🚀",
    noCampaigns: "Aucune campagne publicitaire",
    noCampaignsDesc: "Vous n'avez pas encore créé de campagnes pour cette plateforme. Cliquez sur le bouton de génération IA !",
    editModalTitle: "✏️ Modifier la Campagne",
    newModalTitle: "🚀 Nouvelle Campagne Publicitaire",
    formPlatform: "Plateforme publicitaire",
    formPlatformDesc: "Choisissez où la campagne doit être publiée",
    formName: "Nom de la campagne",
    formNamePlaceholder: "ex. Campagne d'été - CRM IA",
    formHeadline: "Titre (Headline)",
    formHeadlineDesc: "Recommandé jusqu'à 30 caractères pour Google Ads",
    formHeadlinePlaceholder: "ex. CRM Moderne avec IA | Essayez",
    formDesc: "Description publicitaire",
    formDescDesc: "Texte persuasif avec appel à l'action (CTA)",
    formDescPlaceholder: "ex. Augmentez vos ventes et gagnez du temps...",
    formKeywords: "Mots-clés (séparés par des virgules)",
    formKeywordsPlaceholder: "crm, système ia, automatisation, leads",
    formBudget: "Budget quotidien (en PLN)",
    formBudgetDesc: "Montant maximum que vous souhaitez dépenser par jour",
    formStatus: "Statut de la campagne",
    cancel: "Annuler",
    save: "Enregistrer la campagne",
    statusDraft: "📝 Brouillon (DRAFT)",
    statusApproved: "✅ Approuvée (APPROVED)",
    statusPublished: "🚀 Publiée (PUBLISHED)",
    confirmDelete: "Voulez-vous vraiment supprimer cette campagne ?",
    publishingTo: "🚀 Envoi de la campagne à",
    publishingSuccess: "La campagne sera activée une fois connectée à votre compte publicitaire.",
    generateError: "Échec de la génération des campagnes avec l'IA.",
    aiTasks: "AI Tasks",
    aiTasksDrawerTitle: "Background AI Tasks",
    noTasks: "No background tasks.",
    statusLabel: "Status:",
  },
  es: {
    title: "Campañas de Anuncios (Google Ads & Meta)",
    desc: "Generación automática de campañas basadas en sus servicios utilizando el asistente IA con publicación instantánea.",
    generateAi: "Generar con IA 🤖",
    analyzing: "IA analizando servicios...",
    newCampaign: "Nueva Campaña",
    allPlatforms: "Todas las plataformas",
    showing: "Mostrando:",
    campaigns: "campañas",
    headline: "Título del anuncio:",
    keywords: "Palabras clave / Etiquetas:",
    dailyBudget: "Presupuesto diario:",
    perDay: "PLN / día",
    edit: "Editar",
    published: "Publicada",
    publish: "Publicar 🚀",
    noCampaigns: "Sin campañas publicitarias",
    noCampaignsDesc: "Aún no tienes campañas creadas para esta plataforma. ¡Haz clic en el generador IA para crear anuncios!",
    editModalTitle: "✏️ Editar Campaña",
    newModalTitle: "🚀 Nueva Campaña de Anuncios",
    formPlatform: "Plataforma publicitaria",
    formPlatformDesc: "Elige dónde se publicará la campaña",
    formName: "Nombre de la campaña",
    formNamePlaceholder: "ej. Campaña de Verano - CRM IA",
    formHeadline: "Título del anuncio (Headline)",
    formHeadlineDesc: "Recomendado hasta 30 caracteres para Google Ads",
    formHeadlinePlaceholder: "ej. CRM Moderno con IA | Pruébalo",
    formDesc: "Descripción del anuncio",
    formDescDesc: "Texto persuasivo con llamada a la acción (CTA)",
    formDescPlaceholder: "ej. Aumenta tus ventas y ahorra tiempo...",
    formKeywords: "Palabras clave (separadas por comas)",
    formKeywordsPlaceholder: "crm, sistema ia, automatización, leads",
    formBudget: "Presupuesto diario (en PLN)",
    formBudgetDesc: "Cantidad máxima que deseas gastar por día",
    formStatus: "Estado de la campaña",
    cancel: "Cancelar",
    save: "Guardar Campaña",
    statusDraft: "📝 Borrador (DRAFT)",
    statusApproved: "✅ Aprobada (APPROVED)",
    statusPublished: "🚀 Publicada (PUBLISHED)",
    confirmDelete: "¿Estás seguro de que quieres eliminar esta campaña?",
    publishingTo: "🚀 Enviando campaña a",
    publishingSuccess: "La campaña se activará una vez conectada a tu cuenta publicitaria.",
    generateError: "Error al generar campañas con IA.",
    aiTasks: "KI-Aufgaben",
    aiTasksDrawerTitle: "KI-Hintergrundaufgaben",
    noTasks: "Keine Aufgaben.",
    statusLabel: "Status:",
  },
  it: {
    title: "Campagne Pubblicitarie (Google Ads & Meta)",
    desc: "Creazione automatica di campagne basate sui tuoi servizi tramite assistente IA con pubblicazione istantanea.",
    generateAi: "Genera con IA 🤖",
    analyzing: "IA analizza i servizi...",
    newCampaign: "Nuova Campagna",
    allPlatforms: "Tutte le piattaforme",
    showing: "Visualizzati:",
    campaigns: "campagne",
    headline: "Titolo annuncio:",
    keywords: "Parole chiave / Tag:",
    dailyBudget: "Budget giornaliero:",
    perDay: "PLN / giorno",
    edit: "Modifica",
    published: "Pubblicata",
    publish: "Pubblica 🚀",
    noCampaigns: "Nessuna campagna pubblicitaria",
    noCampaignsDesc: "Non hai ancora campagne create per questa piattaforma. Clicca sul pulsante del generatore IA!",
    editModalTitle: "✏️ Modifica Campagna",
    newModalTitle: "🚀 Nuova Campagna Pubblicitaria",
    formPlatform: "Piattaforma pubblicitaria",
    formPlatformDesc: "Scegli dove pubblicare la campagna",
    formName: "Nome della campagna",
    formNamePlaceholder: "es. Campagna Estiva - CRM IA",
    formHeadline: "Titolo annuncio (Headline)",
    formHeadlineDesc: "Consigliato fino a 30 caratteri per Google Ads",
    formHeadlinePlaceholder: "es. CRM Moderno con IA | Provalo ora",
    formDesc: "Testo dell'annuncio",
    formDescDesc: "Testo persuasivo con invito all'azione (CTA)",
    formDescPlaceholder: "es. Aumenta le vendite della tua azienda e risparmia tempo...",
    formKeywords: "Parole chiave (separate da virgola)",
    formKeywordsPlaceholder: "crm, sistema ia, automazione, lead",
    formBudget: "Budget giornaliero (in PLN)",
    formBudgetDesc: "Importo massimo che desideri spendere al giorno",
    formStatus: "Stato della campagna",
    cancel: "Annulla",
    save: "Salva Campagna",
    statusDraft: "📝 Bozza (DRAFT)",
    statusApproved: "✅ Approvata (APPROVED)",
    statusPublished: "🚀 Pubblicata (PUBLISHED)",
    confirmDelete: "Sei sicuro di voler eliminare questa campagna?",
    publishingTo: "🚀 Invio campagna a",
    publishingSuccess: "La campagna sarà attivata una volta collegata all'account pubblicitario.",
    generateError: "Impossibile generare campagne con IA.",
    aiTasks: "Tâches IA",
    aiTasksDrawerTitle: "Tâches IA en arrière-plan",
    noTasks: "Aucune tâche.",
    statusLabel: "Statut:",
  },
  pt: {
    title: "Campanhas de Anúncios (Google Ads & Meta)",
    desc: "Geração automática de campanhas com base em seus serviços usando o assistente IA com publicação instantânea.",
    generateAi: "Gerar com IA 🤖",
    analyzing: "IA analisando serviços...",
    newCampaign: "Nova Campanha",
    allPlatforms: "Todas as plataformas",
    showing: "Exibindo:",
    campaigns: "campanhas",
    headline: "Título do anúncio:",
    keywords: "Palavras-chave / Tags:",
    dailyBudget: "Orçamento diário:",
    perDay: "PLN / dia",
    edit: "Editar",
    published: "Publicada",
    publish: "Publicar 🚀",
    noCampaigns: "Sem campanhas publicitárias",
    noCampaignsDesc: "Você ainda não possui campanhas criadas para esta plataforma. Clique no botão de geração IA!",
    editModalTitle: "✏️ Editar Campanha",
    newModalTitle: "🚀 Nova Campanha de Anúncios",
    formPlatform: "Plataforma de anúncios",
    formPlatformDesc: "Escolha onde a campanha deve ser publicada",
    formName: "Nome da campanha",
    formNamePlaceholder: "ex. Campanha de Verão - CRM IA",
    formHeadline: "Título (Headline)",
    formHeadlineDesc: "Recomendado até 30 caracteres para Google Ads",
    formHeadlinePlaceholder: "ex. CRM Moderno com IA | Experimente",
    formDesc: "Descrição do anúncio",
    formDescDesc: "Texto persuasivo com chamada para ação (CTA)",
    formDescPlaceholder: "ex. Aumente as vendas de sua empresa e economize tempo...",
    formKeywords: "Palavras-chave (separadas por vírgula)",
    formKeywordsPlaceholder: "crm, sistema ia, automação, leads",
    formBudget: "Orçamento diário (em PLN)",
    formBudgetDesc: "Valor máximo que você deseja gastar por dia",
    formStatus: "Status da campanha",
    cancel: "Cancelar",
    save: "Salvar Campanha",
    statusDraft: "📝 Rascunho (DRAFT)",
    statusApproved: "✅ Aprovada (APPROVED)",
    statusPublished: "🚀 Publicada (PUBLISHED)",
    confirmDelete: "Tem certeza de que deseja excluir esta campanha?",
    publishingTo: "🚀 Enviando campanha para",
    publishingSuccess: "A campanha será ativada assim que for conectada à conta de anúncios.",
    generateError: "Falha ao gerar campanhas com IA.",
    aiTasks: "Tareas de IA",
    aiTasksDrawerTitle: "Tareas de IA en segundo plano",
    noTasks: "Sin tareas.",
    statusLabel: "Estado:",
  },
  nl: {
    title: "Advertentiecampagnes (Google Ads & Meta)",
    desc: "Automatische campagregeneratie op basis van uw diensten via de AI-assistent met directe publicatie.",
    generateAi: "Genereren met AI 🤖",
    analyzing: "AI analyseert diensten...",
    newCampaign: "Nieuwe Campagne",
    allPlatforms: "Alle platforms",
    showing: "Weergave:",
    campaigns: "campagnes",
    headline: "Advertentietitel:",
    keywords: "Trefwoorden / Tags:",
    dailyBudget: "Dagbudget:",
    perDay: "PLN / dag",
    edit: "Bewerken",
    published: "Gepubliceerd",
    publish: "Publiceren 🚀",
    noCampaigns: "Geen advertentiecampagnes",
    noCampaignsDesc: "U heeft nog geen campagnes gemaakt voor dit platform. Klik op de AI-generator knop!",
    editModalTitle: "✏️ Campagne bewerken",
    newModalTitle: "🚀 Nieuwe Advertentiecampagne",
    formPlatform: "Advertentieplatform",
    formPlatformDesc: "Kies waar de campagne gepubliceerd moet worden",
    formName: "Campagnenaam",
    formNamePlaceholder: "bijv. Zomercampagne - CRM AI",
    formHeadline: "Advertentietitel (Headline)",
    formHeadlineDesc: "Aanbevolen tot 30 tekens voor Google Ads",
    formHeadlinePlaceholder: "bijv. Modern CRM met AI | Probeer Nu",
    formDesc: "Advertentietekst (Description)",
    formDescDesc: "Overtuigende tekst met Call To Action (CTA)",
    formDescPlaceholder: "bijv. Verhoog uw omzet en bespaar tijd...",
    formKeywords: "Trefwoorden (kommagescheiden)",
    formKeywordsPlaceholder: "crm, ai systeem, automatisering, leads",
    formBudget: "Dagbudget (in PLN)",
    formBudgetDesc: "Maximaal bedrag dat u per dag wilt uitgerven",
    formStatus: "Campagnestatus",
    cancel: "Annuleren",
    save: "Campagne opslaan",
    statusDraft: "📝 Concept (DRAFT)",
    statusApproved: "✅ Goedgekeurd (APPROVED)",
    statusPublished: "🚀 Gepubliceerd (PUBLISHED)",
    confirmDelete: "Weet u zeker dat u deze campagne wilt verwijderen?",
    publishingTo: "🚀 Campagne verzenden naar",
    publishingSuccess: "De campagne wordt geactiveerd zodra deze is gekoppeld aan uw advertentieaccount.",
    generateError: "Mislukt om campagnes te genereren met AI.",
    aiTasks: "Attività IA",
    aiTasksDrawerTitle: "Attività IA in background",
    noTasks: "Nessuna attività.",
    statusLabel: "Stato:",
  },
  sv: {
    title: "Annonskampanjer (Google Ads & Meta)",
    desc: "Automatisk kampanjgenerering baserad på dina tjänster via AI-assistenten med direkt publicering.",
    generateAi: "Generera med AI 🤖",
    analyzing: "AI analyserar tjänster...",
    newCampaign: "Ny Kampanj",
    allPlatforms: "Alla plattformar",
    showing: "Visar:",
    campaigns: "kampanjer",
    headline: "Annonspubrik:",
    keywords: "Nyckelord / Tagg:",
    dailyBudget: "Daglig budget:",
    perDay: "PLN / dag",
    edit: "Redigera",
    published: "Publicerad",
    publish: "Publicera 🚀",
    noCampaigns: "Inga annonskampanjer",
    noCampaignsDesc: "Du har inga kampanjer skapade för denna plattform än. Klicka på AI-generatorns knapp!",
    editModalTitle: "✏️ Redigera Kampanj",
    newModalTitle: "🚀 Ny Annonskampanj",
    formPlatform: "Annonsplattform",
    formPlatformDesc: "Välj var kampanjen ska publiceras",
    formName: "Kampanjnamn",
    formNamePlaceholder: "t.ex. Sommarkampanj - CRM AI",
    formHeadline: "Annonspubrik (Headline)",
    formHeadlineDesc: "Rekommenderat upp till 30 tecken för Google Ads",
    formHeadlinePlaceholder: "t.ex. Modern CRM med AI | Prova Nu",
    formDesc: "Annonstext (Description)",
    formDescDesc: "Övertygande text med uppmaning till handling (CTA)",
    formDescPlaceholder: "t.ex. Öka din försäljning och spara tid...",
    formKeywords: "Nyckelord (kommaseparerade)",
    formKeywordsPlaceholder: "crm, ai system, automatisering, leads",
    formBudget: "Daglig budget (i PLN)",
    formBudgetDesc: "Maximalt belopp du vill spendera per dag",
    formStatus: "Kampanjstatus",
    cancel: "Avbryt",
    save: "Spara Kampanj",
    statusDraft: "📝 Utkast (DRAFT)",
    statusApproved: "✅ Godkänd (APPROVED)",
    statusPublished: "🚀 Publicerad (PUBLISHED)",
    confirmDelete: "Är du säker på att du vill ta bort den här kampanjen?",
    publishingTo: "🚀 Skickar kampanj till",
    publishingSuccess: "Kampanjen kommer att aktiveras när den är ansluten till ditt annonskonto.",
    generateError: "Misslyckades med att generera kampanjer med AI.",
    aiTasks: "Tarefas de IA",
    aiTasksDrawerTitle: "Tarefas de IA em segundo plano",
    noTasks: "Sem tarefas.",
    statusLabel: "Status:",
  },
  cs: {
    title: "Reklamní kampaně (Google Ads & Meta)",
    desc: "Automatické generování kampaní na základě vašich služeb pomocí AI asistenta s okamžitým publikováním.",
    generateAi: "Generovat s AI 🤖",
    analyzing: "AI analyzuje služby...",
    newCampaign: "Nová Kampaň",
    allPlatforms: "Všechny platformy",
    showing: "Zobrazeno:",
    campaigns: "kampaní",
    headline: "Nadpis reklamy:",
    keywords: "Klíčová slova / Štítky:",
    dailyBudget: "Denní rozpočet:",
    perDay: "PLN / den",
    edit: "Upravit",
    published: "Publikováno",
    publish: "Publikovat 🚀",
    noCampaigns: "Žádné reklamní kampaně",
    noCampaignsDesc: "Pro tuto platformu nemáte vytvořené žádné kampaně. Klikněte na tlačítko AI generátoru!",
    editModalTitle: "✏️ Upravit Kampaň",
    newModalTitle: "🚀 Nová Reklamní Kampaň",
    formPlatform: "Reklamní platforma",
    formPlatformDesc: "Vyberte, kde má být kampaň publikována",
    formName: "Název kampaně",
    formNamePlaceholder: "např. Letní kampaň - CRM AI",
    formHeadline: "Nadpis reklamy (Headline)",
    formHeadlineDesc: "Doporučeno do 30 znaků pro Google Ads",
    formHeadlinePlaceholder: "např. Moderní CRM s AI | Vyzkoušejte",
    formDesc: "Text reklamy (Description)",
    formDescDesc: "Přesvědčivý text s výzvou k akci (CTA)",
    formDescPlaceholder: "např. Zvyšte prodeje ve firmě a ušetřete čas...",
    formKeywords: "Klíčová slova (oddělená čárkou)",
    formKeywordsPlaceholder: "crm, ai systém, automatizace, leady",
    formBudget: "Denní rozpočet (v PLN)",
    formBudgetDesc: "Maximální částka, kterou chcete denně utratit",
    formStatus: "Stav kampaně",
    cancel: "Zrušit",
    save: "Uložit kampaň",
    statusDraft: "📝 Koncept (DRAFT)",
    statusApproved: "✅ Schváleno (APPROVED)",
    statusPublished: "🚀 Publikováno (PUBLISHED)",
    confirmDelete: "Opravdu chcete tuto kampaň smazat?",
    publishingTo: "🚀 Odesílání kampaně do",
    publishingSuccess: "Kampaň bude aktivována po propojení s vaším reklamním účtem.",
    generateError: "Nepodařilo se vygenerovat kampaně s AI.",
  }
};

export default function AdCampaignsTab({ user, apiBaseUrl }) {
  const { i18n } = useTranslation();
  const lang = (i18n?.language || 'pl').substring(0, 2).toLowerCase();
  const tr = AD_TRANSLATIONS[lang] || AD_TRANSLATIONS.en;

  const [campaigns, setCampaigns] = useState([]);
  const [loading, setLoading] = useState(false);
  const [generatingAi, setGeneratingAi] = useState(false);
  const [aiContext, setAiContext] = useState('');
  const [aiModalOpened, setAiModalOpened] = useState(false);
  const [platformFilter, setPlatformFilter] = useState('ALL');
  
  const [jobs, setJobs] = useState([]);
  const [jobsDrawerOpened, setJobsDrawerOpened] = useState(false);
  
  const [modalOpened, setModalOpened] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [form, setForm] = useState({ 
    platform: 'GOOGLE_ADS', 
    campaignName: '', 
    headline: '', 
    description: '', 
    keywords: '', 
    dailyBudget: 50,
    status: 'DRAFT'
  });

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const res = await fetch(`${apiBaseUrl}/api/ad-campaigns?ownerEmail=${encodeURIComponent(user?.email || 'admin@ailinkb.com')}`);
      if (res.ok) {
        const data = await res.json();
        setCampaigns(data);
      }
    } catch (e) {
      console.error("Błąd pobierania kampanii:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchJobs = async () => {
    try {
      const email = encodeURIComponent(user?.email || 'admin@ailinkb.com');
      const res = await fetch(`${apiBaseUrl}/api/jobs?userEmail=${email}`);
      if (res.ok) {
        const data = await res.json();
        setJobs(data);
      }
    } catch (e) {
      console.error("Błąd pobierania zadań:", e);
    }
  };

  useEffect(() => {
    fetchCampaigns();
    fetchJobs();
  }, [user, apiBaseUrl]);

  const handleGenerateAI = async () => {
    try {
      setGeneratingAi(true);
      const res = await fetch(`${apiBaseUrl}/api/ad-campaigns/generate?ownerEmail=${encodeURIComponent(user?.email || 'admin@ailinkb.com')}&context=${encodeURIComponent(aiContext || '')}`, {
        method: 'POST'
      });
      if (res.ok) {
        setAiContext('');
        setAiModalOpened(false);
        const job = await res.json();
        
        // Pokazujemy drawer od razu po zleceniu
        setJobsDrawerOpened(true);
        fetchJobs();
        
        const pollJob = async () => {
          try {
            const jobRes = await fetch(`${apiBaseUrl}/api/jobs/${job.id}`);
            if (jobRes.ok) {
              const jobData = await jobRes.json();
              if (jobData.status === 'COMPLETED') {
                setGeneratingAi(false);
                fetchCampaigns();
                fetchJobs();
              } else if (jobData.status === 'FAILED') {
                setGeneratingAi(false);
                fetchJobs();
                alert(tr.generateError || "Błąd generowania kampanii");
              } else {
                fetchJobs(); // Update drawer progress
                setTimeout(pollJob, 5000);
              }
            } else {
              setGeneratingAi(false);
            }
          } catch (e) {
            setGeneratingAi(false);
            console.error(e);
          }
        };
        
        setTimeout(pollJob, 5000);
      } else {
        setGeneratingAi(false);
        alert(tr.generateError);
      }
    } catch (e) {
      console.error(e);
      alert(tr.generateError);
      setGeneratingAi(false);
    }
  };

  const handleSave = async () => {
    if (!form.campaignName || !form.headline || !form.description) {
      return;
    }
    
    const payload = {
      ...form,
      ownerEmail: user?.email || 'admin@ailinkb.com'
    };

    try {
      const url = editingId 
        ? `${apiBaseUrl}/api/ad-campaigns/${editingId}`
        : `${apiBaseUrl}/api/ad-campaigns`;
      
      const method = editingId ? 'PUT' : 'POST';

      const res = await fetch(url, {
        method,
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (res.ok) {
        setModalOpened(false);
        resetForm();
        fetchCampaigns();
      }
    } catch (e) {
      console.error("Błąd zapisu kampanii:", e);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(tr.confirmDelete)) return;
    try {
      await fetch(`${apiBaseUrl}/api/ad-campaigns/${id}?ownerEmail=${user.email}`, { method: 'DELETE' });
      setCampaigns(prev => prev.filter(c => c.id !== id));
    } catch (e) { 
      console.error(e); 
    }
  };

  const handlePublish = async (id, platform) => {
    if (platform !== 'GOOGLE_ADS') {
      alert("Na razie wspierana jest tylko publikacja do Google Ads.");
      return;
    }

    try {
      setGeneratingAi(true);
      const res = await fetch(`${apiBaseUrl}/api/ad-campaigns/${id}/publish?ownerEmail=${encodeURIComponent(user?.email || 'admin@ailinkb.com')}`, {
        method: 'POST'
      });
      
      if (res.ok) {
        const job = await res.json();
        setJobsDrawerOpened(true);
        fetchJobs();
        const pollJob = async () => {
          try {
            const jobRes = await fetch(`${apiBaseUrl}/api/jobs/${job.id}`);
            if (jobRes.ok) {
              const jobData = await jobRes.json();
              if (jobData.status === 'COMPLETED') {
                setGeneratingAi(false);
                fetchCampaigns();
                fetchJobs();
                alert("Kampania pomyślnie wysłana do Google Ads!");
              } else if (jobData.status === 'FAILED') {
                setGeneratingAi(false);
                fetchJobs();
                alert("Błąd publikacji: " + (jobData.result || "Sprawdź konfigurację API i klucze."));
              } else {
                fetchJobs();
                setTimeout(pollJob, 5000);
              }
            } else { setGeneratingAi(false); }
          } catch (e) { setGeneratingAi(false); console.error(e); }
        };
        setTimeout(pollJob, 5000);
      } else {
        setGeneratingAi(false);
        alert("Wystąpił błąd podczas zlecania publikacji.");
      }
    } catch (e) {
      console.error(e);
      alert("Błąd połączenia z serwerem.");
      setGeneratingAi(false);
    }
  };

  const handleAnalyzeKeywords = async (id) => {
    try {
      setGeneratingAi(true);
      const res = await fetch(`${apiBaseUrl}/api/ad-campaigns/analyze?campaignId=${id}&ownerEmail=${encodeURIComponent(user?.email || 'admin@ailinkb.com')}`, {
        method: 'POST'
      });
      if (res.ok) {
        const job = await res.json();
        setJobsDrawerOpened(true);
        fetchJobs();
        const pollJob = async () => {
          try {
            const jobRes = await fetch(`${apiBaseUrl}/api/jobs/${job.id}`);
            if (jobRes.ok) {
              const jobData = await jobRes.json();
              if (jobData.status === 'COMPLETED') {
                setGeneratingAi(false);
                fetchCampaigns();
                fetchJobs();
              } else if (jobData.status === 'FAILED') {
                setGeneratingAi(false);
                fetchJobs();
                alert(tr.generateError || "Błąd analizy AI");
              } else {
                fetchJobs();
                setTimeout(pollJob, 5000);
              }
            } else { setGeneratingAi(false); }
          } catch (e) { setGeneratingAi(false); console.error(e); }
        };
        setTimeout(pollJob, 5000);
      } else {
        setGeneratingAi(false);
        alert(tr.generateError);
      }
    } catch (e) {
      console.error(e);
      alert(tr.generateError);
      setGeneratingAi(false);
    }
  };

  const resetForm = () => {
    setEditingId(null);
    setForm({ 
      platform: 'GOOGLE_ADS', 
      campaignName: '', 
      headline: '', 
      description: '', 
      keywords: tr.formKeywordsPlaceholder, 
      dailyBudget: 50,
      status: 'DRAFT'
    });
  };

  const openEditModal = (c) => {
    setEditingId(c.id);
    setForm({
      platform: c.platform || 'GOOGLE_ADS',
      campaignName: c.campaignName || '',
      headline: c.headline || '',
      description: c.description || '',
      keywords: c.keywords || '',
      dailyBudget: c.dailyBudget || 50,
      status: c.status || 'DRAFT'
    });
    setModalOpened(true);
  };

  const filteredCampaigns = campaigns.filter(c => {
    if (platformFilter === 'ALL') return true;
    return c.platform === platformFilter;
  });

  return (
    <Box p="md" style={{ background: 'black', minHeight: 'calc(100vh - 120px)', borderRadius: 12 }}>
      {/* NAGŁÓWEK Z GRADIENTEM I PRZYCISKAMI */}
      <Paper p="xl" radius="md" mb="xl" style={{ 
        background: 'linear-gradient(135deg, #1e1b4b 0%, #312e81 50%, #4338ca 100%)', 
        color: 'white',
        boxShadow: '0 10px 25px -5px rgba(67, 56, 202, 0.4)' 
      }}>
        <Grid align="center">
          <Grid.Col span={{ base: 12, md: 8 }}>
            <Group gap="md">
              <ThemeIcon size={48} radius="md" variant="gradient" gradient={{ from: 'pink', to: 'indigo' }}>
                <IconSpeakerphone size={28} />
              </ThemeIcon>
              <Box>
                <Title order={2} style={{ color: 'white', fontWeight: 700 }}>
                  {tr.title}
                </Title>
                <Text size="sm" c="indigo.1" mt={4}>
                  {tr.desc}
                </Text>
              </Box>
            </Group>
          </Grid.Col>
          <Grid.Col span={{ base: 12, md: 4 }} style={{ display: 'flex', justifyContent: 'flex-end', gap: 10 }}>
            <Button 
              size="md" 
              radius="md" 
              variant="white"
              color="indigo"
              leftSection={<IconHistory size={18} />}
              onClick={() => { fetchJobs(); setJobsDrawerOpened(true); }}
            >
              {tr.aiTasks}
            </Button>
            <Button 
              size="md" 
              radius="md" 
              variant="gradient" 
              gradient={{ from: 'pink', to: 'orange' }} 
              leftSection={generatingAi ? <Loader size={16} color="white" /> : <IconRobot size={20} />}
              onClick={() => setAiModalOpened(true)}
              disabled={generatingAi}
              style={{ boxShadow: '0 4px 12px rgba(236, 72, 153, 0.3)' }}
            >
              {generatingAi ? tr.analyzing : tr.generateAi}
            </Button>
            <Button 
              size="md" 
              radius="md" 
              variant="white" 
              color="indigo" 
              leftSection={<IconPlus size={18} />}
              onClick={() => { resetForm(); setModalOpened(true); }}
            >
              {tr.newCampaign}
            </Button>
          </Grid.Col>
        </Grid>
      </Paper>

      {/* PASEK FILTRÓW */}
      <Group justify="space-between" mb="lg">
        <SegmentedControl
          value={platformFilter}
          onChange={setPlatformFilter}
          data={[
            { label: tr.allPlatforms, value: 'ALL' },
            { label: '🔍 Google Ads', value: 'GOOGLE_ADS' },
            { label: '📱 Facebook / Meta Ads', value: 'FACEBOOK_ADS' },
          ]}
          radius="md"
          size="md"
        />
        <Text size="sm" c="gray.5" fw={500}>
          {tr.showing} {filteredCampaigns.length} {tr.campaigns}
        </Text>
      </Group>

      {/* SIATKA KAMPANII */}
      <SimpleGrid cols={{ base: 1, md: 2, lg: 3 }} spacing="lg">
        {filteredCampaigns.map((c) => {
          const isGoogle = c.platform === 'GOOGLE_ADS';
          const isPublished = c.status === 'PUBLISHED';
          
          return (
            <Card 
              key={c.id} 
              shadow="sm" 
              radius="md" 
              withBorder 
              p="lg" 
              style={{ 
                display: 'flex', 
                flexDirection: 'column', 
                justifyContent: 'space-between',
                transition: 'transform 0.2s ease, box-shadow 0.2s ease',
                '&:hover': { transform: 'translateY(-4px)', boxShadow: '0 12px 24px -10px rgba(0, 0, 0, 0.15)' },
                background: '#141517',
                borderTop: isGoogle ? '4px solid #3b82f6' : '4px solid #6366f1'
              }}
            >
              <Box>
                <Group justify="space-between" mb="xs">
                  <Badge 
                    variant="light" 
                    color={isGoogle ? 'blue' : 'indigo'} 
                    leftSection={isGoogle ? <IconBrandGoogle size={14} /> : <IconBrandFacebook size={14} />}
                    size="md"
                  >
                    {isGoogle ? 'Google Ads' : 'Facebook Ads'}
                  </Badge>
                  <Badge 
                    variant={isPublished ? 'filled' : 'outline'} 
                    color={isPublished ? 'green' : c.status === 'APPROVED' ? 'teal' : 'yellow'}
                  >
                    {c.status || 'DRAFT'}
                  </Badge>
                </Group>

                <Title order={4} mb="xs" style={{ color: 'white', fontWeight: 600 }}>
                  {c.campaignName}
                </Title>

                <Box mb="md" p="sm" style={{ background: '#141517', borderRadius: 8 }}>
                  <Text size="xs" c="gray.5" fw={700} tt="uppercase" mb={4}>{tr.headline}</Text>
                  <Text size="sm" fw={600} c="#e4e5e8">{c.headline}</Text>
                </Box>

                <Text size="sm" c="#a1a1aa" mb="md" lineClamp={3}>
                  {c.description}
                </Text>

                <Divider my="sm" />

                <Box mb="md">
                  <Text size="xs" c="gray.5" fw={700} tt="uppercase" mb={4}>{tr.keywords}</Text>
                  <Group gap={4}>
                    {(c.keywords || '').split(',').slice(0, 4).map((kw, idx) => (
                      <Badge key={idx} size="xs" variant="secondary" color="gray">
                        {kw.trim()}
                      </Badge>
                    ))}
                  </Group>
                </Box>
                
                {(c.intentScore !== null && c.intentScore !== undefined) && (
                  <Alert variant="light" color="indigo" title="AI Bidding Analysis" icon={<IconRobot size={16} />} mb="md">
                    <Group gap="xs" mb={4}>
                      <Badge color="violet" size="sm">Intent: {c.intentScore}/100</Badge>
                      <Badge color="green" size="sm">Max CPC: {c.maxCpcBid} PLN</Badge>
                      <Badge color="orange" size="sm">{c.targetAudience}</Badge>
                    </Group>
                    <Text size="xs" fw={500}>{c.aiBidRecommendation}</Text>
                  </Alert>
                )}
              </Box>

              <Box>
                <Group justify="space-between" mb="sm" p="xs" style={{ background: 'black', borderRadius: 6, border: '1px solid #e2e8f0' }}>
                  <Group gap={6}>
                    <IconTrendingUp size={16} color="#64748b" />
                    <Text size="xs" c="gray.5" fw={600}>{tr.dailyBudget}</Text>
                  </Group>
                  <Text size="sm" fw={700} c="indigo.7">{c.dailyBudget} {tr.perDay}</Text>
                </Group>
                
                <Button 
                  fullWidth 
                  mb="sm" 
                  size="xs" 
                  variant="light" 
                  color="grape" 
                  leftSection={generatingAi ? <Loader size={12}/> : <IconRobot size={14}/>}
                  onClick={() => handleAnalyzeKeywords(c.id)}
                  disabled={generatingAi}
                >
                  Analyze & Optimize Bids (AI)
                </Button>

                <Group gap="xs" grow>
                  <Button 
                    variant="default" 
                    size="sm" 
                    leftSection={<IconEdit size={16} />}
                    onClick={() => openEditModal(c)}
                  >
                    {tr.edit}
                  </Button>
                  <ActionIcon 
                    variant="filled" color="red" 
                    size="lg" 
                    radius="md"
                    onClick={() => handleDelete(c.id)}
                  >
                    <IconTrash size={18} />
                  </ActionIcon>
                  <Button 
                    variant={isPublished ? "light" : "gradient"} 
                    gradient={isGoogle ? { from: 'blue', to: 'cyan' } : { from: 'indigo', to: 'violet' }}
                    size="sm" 
                    leftSection={<IconRocket size={16} />}
                    onClick={() => handlePublish(c.id, c.platform)}
                    disabled={isPublished}
                  >
                    {isPublished ? tr.published : tr.publish}
                  </Button>
                </Group>
              </Box>
            </Card>
          );
        })}
      </SimpleGrid>

      {filteredCampaigns.length === 0 && (
        <Paper p="xl" ta="center" radius="md" withBorder mt="xl" style={{ background: '#141517' }}>
          <ThemeIcon size={64} radius="full" variant="light" color="gray" mx="auto" mb="md">
            <IconSpeakerphone size={32} />
          </ThemeIcon>
          <Title order={4} c="gray.7" mb="xs">{tr.noCampaigns}</Title>
          <Text c="gray.5" size="sm" mb="lg" maxW={400} mx="auto">
            {tr.noCampaignsDesc}
          </Text>
          <Button variant="outline" color="indigo" onClick={handleGenerateAI}>
            {tr.generateAi}
          </Button>
        </Paper>
      )}

      {/* MODAL EDYCJI / TWORZENIA KAMPANII */}
      <Modal 
        opened={modalOpened} 
        onClose={() => setModalOpened(false)} 
        title={<Text fw={700} size="lg">{editingId ? tr.editModalTitle : tr.newModalTitle}</Text>}
        size="lg" 
        radius="md"
      >
        <Stack gap="md">
          <Select
            label={tr.formPlatform}
            description={tr.formPlatformDesc}
            data={[
              { label: '🔍 Google Ads (Wyszukiwarka)', value: 'GOOGLE_ADS' },
              { label: '📱 Facebook / Meta Ads (Social Media)', value: 'FACEBOOK_ADS' }
            ]}
            value={form.platform}
            onChange={(val) => setForm({ ...form, platform: val })}
          />

          <TextInput
            label={tr.formName}
            placeholder={tr.formNamePlaceholder}
            required
            value={form.campaignName}
            onChange={(e) => setForm({ ...form, campaignName: e.target.value })}
          />

          <TextInput
            label={tr.formHeadline}
            description={tr.formHeadlineDesc}
            placeholder={tr.formHeadlinePlaceholder}
            required
            value={form.headline}
            onChange={(e) => setForm({ ...form, headline: e.target.value })}
          />

          <Textarea
            label={tr.formDesc}
            description={tr.formDescDesc}
            placeholder={tr.formDescPlaceholder}
            rows={4}
            required
            value={form.description}
            onChange={(e) => setForm({ ...form, description: e.target.value })}
          />

          <TextInput
            label={tr.formKeywords}
            placeholder={tr.formKeywordsPlaceholder}
            value={form.keywords}
            onChange={(e) => setForm({ ...form, keywords: e.target.value })}
          />

          <NumberInput
            label={tr.formBudget}
            description={tr.formBudgetDesc}
            value={form.dailyBudget}
            onChange={(val) => setForm({ ...form, dailyBudget: val || 0 })}
            min={10}
            step={10}
            suffix=" PLN"
          />

          <Select
            label={tr.formStatus}
            data={[
              { label: tr.statusDraft, value: 'DRAFT' },
              { label: tr.statusApproved, value: 'APPROVED' },
              { label: tr.statusPublished, value: 'PUBLISHED' }
            ]}
            value={form.status}
            onChange={(val) => setForm({ ...form, status: val })}
          />

          <Group justify="flex-end" mt="md">
            <Button variant="default" onClick={() => setModalOpened(false)}>{tr.cancel}</Button>
            <Button color="indigo" onClick={handleSave}>{tr.save}</Button>
          </Group>
        </Stack>
      </Modal>

      {/* MODAL GENEROWANIA Z AI */}
      <Modal 
        opened={aiModalOpened} 
        onClose={() => !generatingAi && setAiModalOpened(false)} 
        title={
          <Group gap="xs">
            <ThemeIcon size={32} radius="md" variant="gradient" gradient={{ from: 'pink', to: 'orange' }}>
              <IconRobot size={20} />
            </ThemeIcon>
            <Text fw={700} size="lg">{tr.generateAiTitle || "Wygeneruj Kampanie Reklamowe z AI"}</Text>
          </Group>
        } 
        centered 
        size="lg"
        radius="md"
      >
        <Box p="sm" style={{ background: 'black', borderRadius: 8, border: '1px solid #e2e8f0' }} mb="md">
          <Text size="sm" c="gray.5">
            {tr.generateAiDesc || "Nasz asystent AI (Agentic AI) przeanalizuje Twoje usługi z cennika i automatycznie przygotuje gotowe nagłówki, opisy oraz propozycje słów kluczowych dla Google Ads i Facebook Ads."}
          </Text>
        </Box>

        <Textarea
          label={tr.aiContextLabel || "💡 Dodatkowy kontekst / wytyczne dla AI (opcjonalnie):"}
          description={tr.aiContextDesc || "Możesz wpisać szczegółowe wskazówki, np. aktualną promocję, grupę docelową lub ton wypowiedzi."}
          placeholder={tr.aiContextPlaceholder || "np. Skup się na promocji jesiennej z rabatem 20% dla klientów B2B, stwórz pilne wezwanie do działania..."}
          value={aiContext}
          onChange={(e) => setAiContext(e.target.value)}
          disabled={generatingAi}
          minRows={3}
          autosize
          maxRows={6}
          mb="xl"
          styles={{
            label: { fontWeight: 600, marginBottom: 4, color: 'white' },
            input: { background: '#141517', border: '1px solid #cbd5e1', borderRadius: 8 }
          }}
        />

        <Group justify="flex-end" gap="sm">
          <Button 
            variant="default" 
            onClick={() => setAiModalOpened(false)} 
            disabled={generatingAi}
            radius="md"
          >
            {tr.cancel}
          </Button>
          <Button 
            variant="gradient" 
            gradient={{ from: 'pink', to: 'orange' }} 
            leftSection={generatingAi ? <Loader size={16} color="white" /> : <IconRobot size={18} />}
            onClick={() => {
              handleGenerateAI();
            }}
            loading={generatingAi}
            disabled={generatingAi}
            radius="md"
            style={{ boxShadow: '0 4px 12px rgba(236, 72, 153, 0.3)' }}
          >
            {generatingAi ? tr.analyzing : (tr.generateAiStart || "✨ Rozpocznij generowanie")}
          </Button>
        </Group>
      </Modal>

      {/* DRAWER Z HISTORIĄ ZADAŃ AI */}
      <Drawer
        opened={jobsDrawerOpened}
        onClose={() => setJobsDrawerOpened(false)}
        title={<Text fw={700} size="lg">{tr.aiTasksDrawerTitle}</Text>}
        position="right"
        size="md"
      >
        <ScrollArea h="calc(100vh - 100px)">
          {jobs.length === 0 ? (
            <Text c="gray.5" ta="center" mt="xl">{tr.noTasks}</Text>
          ) : (
            <Timeline active={-1} bulletSize={24} lineWidth={2} mt="md">
              {jobs.map(job => {
                const isCompleted = job.status === 'COMPLETED';
                const isFailed = job.status === 'FAILED';
                const isRunning = job.status === 'RUNNING' || job.status === 'PENDING';
                
                let color = 'gray';
                let icon = <IconHistory size={12} />;
                if (isCompleted) { color = 'teal'; icon = <IconCheck size={12} />; }
                if (isFailed) { color = 'red'; icon = <IconX size={12} />; }
                if (isRunning) { color = 'blue'; icon = <IconLoader2 size={12} className="spin" />; }

                
                let title = (job.jobType === 'GENEROWANIE KAMPANII (AI)' || job.jobType === 'GENERATE_AD_CAMPAIGN') ? (tr.jobTypeGenerate || "Generowanie Kampanii (AI)") : job.jobType;
                let resultText = job.result;
                if (resultText) {
                  if (resultText.includes('Oczekiwanie')) resultText = tr.jobResultWaiting || resultText;
                  else if (resultText.includes('Successfully generated') || resultText.includes('Wygenerowano')) resultText = tr.jobResultSuccess || resultText;
                  else if (resultText.includes('No campaigns') || resultText.includes('No campaign')) resultText = tr.jobResultNoCampaigns || resultText;
                }

                return (
                  <Timeline.Item key={job.id} title={title} bullet={icon} color={color}>
                    <Text c="gray.5" size="sm" mt={4}>{tr.statusLabel} <Badge color={color} size="xs">{job.status}</Badge></Text>
                    {resultText && <Text size="xs" mt={4}>{resultText}</Text>}
                    <Text size="xs" mt={4} c="gray.5">
                      {new Date(job.createdAt).toLocaleString()}
                    </Text>
                  </Timeline.Item>
                );
              })}
            </Timeline>
          )}
        </ScrollArea>
      </Drawer>
      
      <style>{`
        .spin { animation: spin 2s linear infinite; }
        @keyframes spin { 100% { transform: rotate(360deg); } }
      `}</style>
    </Box>
  );
}
